import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  DashboardPortfolioReport,
  DisplayDensity,
  FilterState,
  StatusBarStats,
  TabId,
} from './types';
import { DEFAULT_FILTER_STATE } from './types';

export type PinnedPortfolioDeepLink = {
  portfolioReportId: string;
  report: DashboardPortfolioReport;
};

type TradingHubContextType = {
  currentTab: TabId;
  setCurrentTab: (tab: TabId) => void;

  density: DisplayDensity;
  setDensity: (d: DisplayDensity) => void;

  statusBarStats: StatusBarStats;
  updateStatusBarStats: (stats: Partial<StatusBarStats>) => void;

  filters: FilterState;
  updateFilters: (updates: Partial<FilterState>) => void;
  resetFilters: () => void;

  refreshKey: number;
  triggerRefresh: () => void;

  portfolioStatusPreFilter: string | null;
  setPortfolioStatusPreFilter: (filter: string | null) => void;
  tradeStatusPreFilter: string | null;
  setTradeStatusPreFilter: (filter: string | null) => void;
  operationsPreFilter: string | null;
  setOperationsPreFilter: (filter: string | null) => void;

  insightSidebarOpen: boolean;
  setInsightSidebarOpen: (open: boolean) => void;

  activeFlowStep:
    | 'idle'
    | 'selecting'
    | 'staging'
    | 'verifying'
    | 'batching'
    | 'executing';
  setActiveFlowStep: (
    step:
      | 'idle'
      | 'selecting'
      | 'staging'
      | 'verifying'
      | 'batching'
      | 'executing',
  ) => void;
  pendingBulkTradeRunId: string | null;
  setPendingBulkTradeRunId: (id: string | null) => void;
  recentlyCreatedSTRIds: string[];
  setRecentlyCreatedSTRIds: (ids: string[]) => void;
  lastBulkCreationTime: number | null;
  setLastBulkCreationTime: (time: number | null) => void;

  pinnedPortfolioDeepLink: PinnedPortfolioDeepLink | null;
  setPinnedPortfolioDeepLink: (link: PinnedPortfolioDeepLink | null) => void;

  navigateTo: (path: string) => void;
};

const defaultStats: StatusBarStats = {
  needsAction: 0,
  inProgress: 0,
  onTrack: 0,
  totalAum: 0,
  activeOperations: 0,
  tradesInFlight: 0,
};

const TradingHubContext = createContext<TradingHubContextType>({
  currentTab: 'today',
  setCurrentTab: () => {},
  density: 'compact',
  setDensity: () => {},
  statusBarStats: defaultStats,
  updateStatusBarStats: () => {},
  filters: DEFAULT_FILTER_STATE,
  updateFilters: () => {},
  resetFilters: () => {},
  refreshKey: 0,
  triggerRefresh: () => {},
  portfolioStatusPreFilter: null,
  setPortfolioStatusPreFilter: () => {},
  tradeStatusPreFilter: null,
  setTradeStatusPreFilter: () => {},
  operationsPreFilter: null,
  setOperationsPreFilter: () => {},
  insightSidebarOpen: false,
  setInsightSidebarOpen: () => {},
  activeFlowStep: 'idle',
  setActiveFlowStep: () => {},
  pendingBulkTradeRunId: null,
  setPendingBulkTradeRunId: () => {},
  recentlyCreatedSTRIds: [],
  setRecentlyCreatedSTRIds: () => {},
  lastBulkCreationTime: null,
  setLastBulkCreationTime: () => {},
  pinnedPortfolioDeepLink: null,
  setPinnedPortfolioDeepLink: () => {},
  navigateTo: () => {},
});

export const TradingHubProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currentTab, setCurrentTab] = useState<TabId>('today');
  const [density, setDensity] = useState<DisplayDensity>('compact');
  const [statusBarStats, setStatusBarStats] =
    useState<StatusBarStats>(defaultStats);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [refreshKey, setRefreshKey] = useState(0);
  const [portfolioStatusPreFilter, setPortfolioStatusPreFilter] = useState<
    string | null
  >(null);
  const [tradeStatusPreFilter, setTradeStatusPreFilter] = useState<
    string | null
  >(null);
  const [operationsPreFilter, setOperationsPreFilter] = useState<string | null>(
    null,
  );
  const [insightSidebarOpen, setInsightSidebarOpen] = useState(false);
  const [activeFlowStep, setActiveFlowStep] = useState<
    'idle' | 'selecting' | 'staging' | 'verifying' | 'batching' | 'executing'
  >('idle');
  const [pendingBulkTradeRunId, setPendingBulkTradeRunId] = useState<
    string | null
  >(null);
  const [recentlyCreatedSTRIds, setRecentlyCreatedSTRIds] = useState<string[]>(
    [],
  );
  const [lastBulkCreationTime, setLastBulkCreationTime] = useState<
    number | null
  >(null);
  const [pinnedPortfolioDeepLink, setPinnedPortfolioDeepLink] =
    useState<PinnedPortfolioDeepLink | null>(null);
  const routerNavigate = useNavigate();
  const navigateTo = useCallback(
    (path: string) => routerNavigate(path),
    [routerNavigate],
  );

  const updateStatusBarStats = useCallback(
    (updates: Partial<StatusBarStats>) => {
      setStatusBarStats((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const updateFilters = useCallback((updates: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTER_STATE);
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const value = useMemo(
    () => ({
      currentTab,
      setCurrentTab,
      density,
      setDensity,
      statusBarStats,
      updateStatusBarStats,
      filters,
      updateFilters,
      resetFilters,
      refreshKey,
      triggerRefresh,
      portfolioStatusPreFilter,
      setPortfolioStatusPreFilter,
      tradeStatusPreFilter,
      setTradeStatusPreFilter,
      operationsPreFilter,
      setOperationsPreFilter,
      insightSidebarOpen,
      setInsightSidebarOpen,
      activeFlowStep,
      setActiveFlowStep,
      pendingBulkTradeRunId,
      setPendingBulkTradeRunId,
      recentlyCreatedSTRIds,
      setRecentlyCreatedSTRIds,
      lastBulkCreationTime,
      setLastBulkCreationTime,
      pinnedPortfolioDeepLink,
      setPinnedPortfolioDeepLink,
      navigateTo,
    }),
    [
      currentTab,
      density,
      statusBarStats,
      updateStatusBarStats,
      filters,
      updateFilters,
      resetFilters,
      refreshKey,
      triggerRefresh,
      portfolioStatusPreFilter,
      tradeStatusPreFilter,
      operationsPreFilter,
      insightSidebarOpen,
      activeFlowStep,
      pendingBulkTradeRunId,
      recentlyCreatedSTRIds,
      lastBulkCreationTime,
      pinnedPortfolioDeepLink,
      navigateTo,
    ],
  );

  return (
    <TradingHubContext.Provider value={value}>
      {children}
    </TradingHubContext.Provider>
  );
};

export const useTradingHub = () => {
  const context = useContext(TradingHubContext);
  if (!context) {
    throw new Error('useTradingHub must be used within TradingHubProvider');
  }
  return context;
};

export default TradingHubContext;
